import { type IRequestForm, type TRequestStepField } from './@interfaces';

/**
 * ECA Digital (RAD-44): CPF test on the order form.
 *
 * The CPF field is injected on the client, only for the categories under test
 * and only while the `order-cpf-test` feature flag is on. The backend accepts
 * the field as optional, so nothing here is required for the order to be created.
 */
export const ORDER_CPF_TEST_SLUGS = [
  'reformas-e-reparos/pedreiro/portas-e-janelas',
  'reformas-e-reparos/marceneiro/reparo-e-acabamento',
];

export const CPF_FIELD_NAME = 'cpf';

const TERMS_FIELD_NAME = 'terms';

export const CPF_FIELD: TRequestStepField = {
  title: 'CPF',
  id: CPF_FIELD_NAME,
  name: CPF_FIELD_NAME,
  type: 'text',
  placeholder: '000.000.000-00',
  required: true,
  value: null,
  wrapperClassName: 'form__field input',
  mask: '000.000.000-00',
  regexPattern: '^\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}$',
  minLength: 14,
};

export const isOrderCpfTestCategory = (slug?: string | null): boolean =>
  !!slug && ORDER_CPF_TEST_SLUGS.includes(slug);

interface IShouldAskCpfParams {
  flagEnabled: boolean;
  slug?: string | null;
  hasCpf: boolean;
}

export const shouldAskCpf = ({
  flagEnabled,
  slug,
  hasCpf,
}: IShouldAskCpfParams): boolean =>
  flagEnabled && isOrderCpfTestCategory(slug) && !hasCpf;

const hasCpfField = (fields: TRequestStepField[]) =>
  fields.some((field) => field.name === CPF_FIELD_NAME);

/**
 * Returns a copy of the form with the CPF field in the last step, right before
 * the terms checkbox. Keeps the number of steps untouched because
 * `react-json-form` assumes a fixed number of personal-data steps at the end.
 */
export const addCpfField = (form: IRequestForm): IRequestForm => {
  if (!form?.steps?.length) {
    return form;
  }

  const lastIndex = form.steps.length - 1;
  const lastStep = form.steps[lastIndex];

  if (hasCpfField(lastStep.fields)) {
    return form;
  }

  const termsIndex = lastStep.fields.findIndex(
    (field) => field.name === TERMS_FIELD_NAME
  );
  const insertAt = termsIndex === -1 ? lastStep.fields.length : termsIndex;

  const fields = [
    ...lastStep.fields.slice(0, insertAt),
    { ...CPF_FIELD },
    ...lastStep.fields.slice(insertAt),
  ];

  const steps = [...form.steps];
  steps[lastIndex] = { ...lastStep, fields };

  return { ...form, steps };
};
